import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";
import imgDashboard from "@/assets/features/feature-dashboard.jpg";
import imgStrategy from "@/assets/features/feature-strategy.jpg";
import imgRisk from "@/assets/features/feature-risk.jpg";
import imgBehavior from "@/assets/features/feature-behavior.jpg";
import imgChecklist from "@/assets/features/feature-checklist.jpg";
import imgPlan from "@/assets/features/feature-plan.jpg";
import imgJrx from "@/assets/features/feature-jrx.jpg";
import imgBroker from "@/assets/features/feature-broker.jpg";
import imgReview from "@/assets/features/feature-review.jpg";
import imgAnalytics from "@/assets/features/feature-analytics.jpg";
import imgScore from "@/assets/features/feature-score.jpg";
import imgResume from "@/assets/features/feature-resume.jpg";
import imgAssistant from "@/assets/features/feature-assistant.jpg";
import imgSessions from "@/assets/features/feature-sessions.jpg";
import imgCalendar from "@/assets/features/feature-calendar.jpg";
import imgNews from "@/assets/features/feature-news.jpg";
import imgFundamental from "@/assets/features/feature-fundamental.jpg";
import imgTour from "@/assets/features/feature-tour.jpg";

type Feature = {
  image: string;
  title: string;
  description: string;
};

const features: Feature[] = [
  {
    image: imgDashboard,
    title: "All-in-One Trader Dashboard",
    description:
      "Financial dashboard, risk dashboard and live account status in a single, focused view. See P&L, exposure, drawdown and account health at a glance — no tab-switching, no spreadsheets.",
  },
  {
    image: imgStrategy,
    title: "Custom Strategies & Setups",
    description:
      "Define your own trading strategies and setup playbooks with the precision a professional desk demands. Each strategy becomes a reusable, testable building block tied to your real performance data.",
  },
  {
    image: imgRisk,
    title: "Multi-Layer Risk Plans",
    description:
      "Configure risk at the account level, the trade level, and the time-window level. JouRex enforces your boundaries automatically, so a bad day never becomes a blown account.",
  },
  {
    image: imgBehavior,
    title: "Behavior, Psychology & Execution Profiles",
    description:
      "Model your behavioral patterns, emotional triggers and execution rules as living profiles. JouRex turns soft skills into structured, measurable inputs that drive smarter decisions.",
  },
  {
    image: imgChecklist,
    title: "Trading Guidelines, Playbooks & Checklists",
    description:
      "Build your personal trading guideline, a tactical playbook and pre-trade checklists. Every trade is screened against your own standards before a single order is sent.",
  },
  {
    image: imgPlan,
    title: "Unified Trading Plan",
    description:
      "Compose a complete trading plan that ties together strategy, setup, risk, behavior and execution. One coherent plan — not scattered notes — guiding every market session.",
  },
  {
    image: imgJrx,
    title: "Portable .JRX Plan Files",
    description:
      "Save your full trading plan as a unique .JRX file (a structured JSON package) containing strategy, setups, risk, behavior and execution. Re-import it anytime — JouRex understands it natively.",
  },
  {
    image: imgBroker,
    title: "Direct Broker Integration",
    description:
      "Connect to MetaTrader 4, MetaTrader 5 and cTrader. Every executed trade flows into JouRex automatically as a fully populated journal entry — no manual logging, no missed fills.",
  },
  {
    image: imgReview,
    title: "Qualitative Post-Trade Review",
    description:
      "After execution, enrich each trade with qualitative inputs — behavior, risk decisions, strategy adherence and plan deviations — to track the human side of your performance over time.",
  },
  {
    image: imgAnalytics,
    title: "Multi-Lens Journal Analytics",
    description:
      "Analyze your journal across performance, risk, behavior, plan adherence, strategy, time-of-day, instrument, mistakes and lessons learned. Real intelligence — not vanity charts.",
  },
  {
    image: imgScore,
    title: "Professional Trader Scoring",
    description:
      "An advanced scoring engine grades you on plan discipline, behavioral consistency and financial performance — converging into a single, reliable trader score that reflects how you really trade.",
  },
  {
    image: imgResume,
    title: "Universal Public Trader Resume",
    description:
      "Share your verified scores publicly and build a credible trading resume. JouRex becomes your universal performance passport — recognized across the trading community.",
  },
  {
    image: imgAssistant,
    title: "Live Trade Assistant",
    description:
      "Your real-time co-pilot: see all open positions, monitor live exposure and act directly from JouRex. Actions sync instantly to your trading platform with zero latency lag.",
  },
  {
    image: imgSessions,
    title: "Global Bank Sessions Clock",
    description:
      "A market-watch clock for every major banking session — opens, closes and overlaps. Always know which liquidity window you're actually trading in.",
  },
  {
    image: imgCalendar,
    title: "Smart Economic Calendar",
    description:
      "An intelligent economic calendar that doesn't just list events — it scores their expected impact on each currency, so you can plan around volatility instead of reacting to it.",
  },
  {
    image: imgNews,
    title: "AI News, Geopolitics & Speech Intelligence",
    description:
      "AI-driven analysis of major economic news, wars and tensions, and speeches by key figures — translated into clear directional impact on your traded currencies.",
  },
  {
    image: imgFundamental,
    title: "AI Fundamental Dashboard",
    description:
      "A fundamental analysis dashboard powered by AI, delivering structured guidelines and macro context to support every trading decision with real reasoning.",
  },
  {
    image: imgTour,
    title: "Smart Tour Guide Engine",
    description:
      "An intelligent guided-tour engine helps new traders onboard fast — discovering features and workflows naturally, without manuals or steep learning curves.",
  },
];

export const TradingCenterFeatures = () => {
  // Build a duplicated track for seamless looping
  const track = [...features, ...features];

  // Cards-per-view (responsive): 1 on mobile, 2 on sm, 3 on md, 4 on lg+
  const [perView, setPerView] = useState(4);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(1);
      else if (w < 768) setPerView(2);
      else if (w < 1280) setPerView(3);
      else setPerView(4);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = window.setInterval(() => {
      setIndex((i) => i + 1);
    }, 5000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [paused]);

  // Seamless reset when we cross the original-length boundary
  const [withTransition, setWithTransition] = useState(true);
  useEffect(() => {
    if (index >= features.length) {
      const t = window.setTimeout(() => {
        setWithTransition(false);
        setIndex(0);
        requestAnimationFrame(() => requestAnimationFrame(() => setWithTransition(true)));
      }, 700);
      return () => window.clearTimeout(t);
    }
    if (index < 0) {
      const t = window.setTimeout(() => {
        setWithTransition(false);
        setIndex(features.length - 1);
        requestAnimationFrame(() => requestAnimationFrame(() => setWithTransition(true)));
      }, 700);
      return () => window.clearTimeout(t);
    }
  }, [index]);

  const slidePercent = 100 / perView;

  const goPrev = () => setIndex((i) => i - 1);
  const goNext = () => setIndex((i) => i + 1);

  return (
    <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-card to-primary/5 ring-1 ring-emerald-200/60 shadow-card">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

        <div className="relative p-8 lg:p-14">
          <div className="max-w-3xl mb-8">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-emerald-700 uppercase mb-3 bg-emerald-500/10 px-3 py-1 rounded-full">
              <LayoutGrid className="h-3 w-3" />
              JouRex Key Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Everything a serious trader needs — in one operating system.
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              From planning and execution to behavior, risk and intelligence — JouRex unifies the
              entire trading lifecycle into one professional workspace.
            </p>
          </div>

          <div
            className="relative rounded-2xl bg-card/60 ring-1 ring-border/60 shadow-card p-4 sm:p-6 min-h-[480px]"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
        {/* Prev arrow */}
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous"
          className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-background/95 ring-1 ring-border shadow-elevated hover:bg-background hover:scale-105 transition-all flex items-center justify-center text-foreground"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
        </button>

        {/* Next arrow */}
        <button
          type="button"
          onClick={goNext}
          aria-label="Next"
          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-background/95 ring-1 ring-border shadow-elevated hover:bg-background hover:scale-105 transition-all flex items-center justify-center text-foreground"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
        </button>

        <div className="overflow-hidden">
          <div
            className="flex"
            style={{
              transform: `translateX(-${index * slidePercent}%)`,
              transition: withTransition ? "transform 700ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
            }}
          >
            {track.map((f, i) => (
              <div
                key={`${f.title}-${i}`}
                className="shrink-0 px-2 sm:px-3"
                style={{ width: `${slidePercent}%` }}
              >
                <article className="h-full rounded-xl border border-border/60 bg-background overflow-hidden shadow-sm hover:shadow-elevated hover:-translate-y-0.5 transition-all flex flex-col min-h-[440px]">
                  <div className="relative w-full h-44 lg:h-48 overflow-hidden bg-muted">
                    <img
                      src={f.image}
                      alt={f.title}
                      loading="lazy"
                      width={768}
                      height={512}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5 lg:p-6 flex flex-col gap-3 flex-1">
                    <h3 className="text-base lg:text-lg font-bold text-foreground leading-snug">
                      {f.title}
                    </h3>
                    <p className="text-[15px] text-foreground/80 leading-relaxed font-medium">
                      {f.description}
                    </p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        {/* Progress dots */}
        <div className="mt-5 flex items-center justify-center gap-1.5">
          {features.map((_, i) => {
            const active = i === ((index % features.length) + features.length) % features.length;
            return (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  active ? "w-6 bg-emerald-600" : "w-1.5 bg-border"
                }`}
              />
            );
          })}
        </div>
        </div>
        </div>
      </div>
    </section>
  );
};
